import React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, ShieldCheck, Settings, TrendingUp, Cog, Compass } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useReveal } from "./Home";
import Seo from "../components/Seo";

const ICONS = { ShieldCheck, Settings, TrendingUp, Cog, Compass };

const About = () => {
  const navigate = useNavigate();
  const ref = useReveal();
  const { t, lp } = useLang();

  return (
    <div ref={ref}>
      <Seo title={t.seo.about.title} description={t.seo.about.description} />
      <section className="relative overflow-hidden bg-[#0f1a10] pb-20 pt-40">
        <div className="absolute inset-0 opacity-20">
          <img src={t.hero.slides[1]} alt="" className="h-full w-full object-cover grayscale" />
        </div>
        <div className="relative mx-auto max-w-[1200px] px-6">
          <span className="text-sm font-semibold uppercase tracking-widest text-[#F26522]">{t.about.eyebrow}</span>
          <h1 className="mt-3 max-w-3xl text-4xl font-extrabold leading-tight text-white lg:text-5xl">{t.about.title}</h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-neutral-300">{t.about.subtitle}</p>
        </div>
      </section>

      <section className="mx-auto max-w-[1200px] px-6 py-20 lg:py-24">
        <div className="reveal grid gap-8 md:grid-cols-2">
          {t.intro.paragraphs.map((p) => (
            <p key={p.slice(0, 40)} className="text-[15px] leading-relaxed text-neutral-600">{p}</p>
          ))}
        </div>
        <div className="reveal mt-16 grid gap-6 rounded-2xl bg-neutral-50 p-10 sm:grid-cols-2 lg:grid-cols-4">
          {t.about.stats.map((s) => (
            <div key={s.label} className="text-center">
              <div className="whitespace-nowrap text-3xl font-extrabold text-[#F26522]">{s.value}</div>
              <div className="mt-2 text-sm font-medium text-neutral-600">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-neutral-50 py-20 lg:py-24">
        <div className="mx-auto max-w-[1200px] px-6">
          <div className="reveal mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-bold text-neutral-900 lg:text-4xl">{t.expertise.title}</h2>
            <p className="mt-4 text-[15px] leading-relaxed text-neutral-600">{t.expertise.intro}</p>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {t.expertise.items.map((item, i) => {
              const Icon = ICONS[item.icon];
              return (
                <div key={item.title} style={{ animationDelay: `${i * 70}ms` }}
                  className="reveal group rounded-xl border border-neutral-100 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-lg bg-[#F26522]/10 text-[#F26522] transition-colors duration-300 group-hover:bg-[#F26522] group-hover:text-white">
                    {Icon && <Icon className="h-6 w-6" />}
                  </div>
                  <h3 className="text-lg font-bold text-neutral-900">{item.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-neutral-600">{item.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-[#0f1a10] py-24">
        <div className="reveal mx-auto max-w-3xl px-6 text-center">
          <h2 className="text-3xl font-bold text-white lg:text-4xl">{t.commitment.title}</h2>
          <p className="mt-6 text-lg leading-relaxed text-neutral-300">{t.commitment.text}</p>
          <button onClick={() => navigate(lp("/contact"))}
            className="group mt-8 inline-flex items-center gap-2 rounded-md bg-[#F26522] px-8 py-4 text-base font-semibold text-white transition-all duration-300 hover:bg-[#d9561a] hover:shadow-xl">
            {t.commitment.cta}
            <ChevronRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </div>
      </section>
    </div>
  );
};

export default About;
